import { format } from 'date-fns'
import type { TFunction } from 'i18next'
import type {
    IUserTimelineItem,
    ITimelineLocation,
    TimelineKind,
    TimelineUnavailableReason
} from '@/api/user/getUserDailyTimeline.ts'

const KIND_COLORS: Record<TimelineKind, string> = {
    status: '#2966F4',
    emergency: '#FF003D',
    fall: '#7C4DFF',
    bio: '#FF7D33',
    emergencyTest: '#536176'
}

export const timelineKindLabel = (kind: TimelineKind, t: TFunction): string | null => {
    switch (kind) {
        case 'emergency':
            return t('users.positionTimeline.kind.emergency')
        case 'fall':
            return t('notification.sosModal.list.item.fall')
        case 'bio':
            return t('users.positionTimeline.kind.bio')
        case 'emergencyTest':
            return t('users.positionTimeline.kind.emergencyTest')
        default:
            return null
    }
}

export const timelineKindColor = (kind: TimelineKind): string => KIND_COLORS[kind] ?? KIND_COLORS.status

export const unavailableLabel = (reason: TimelineUnavailableReason | null | undefined, t: TFunction): string => {
    const base = t('users.positionTimeline.unavailable')
    switch (reason) {
        case 'lbs_not_found':
        case 'lbs_error':
            return `${base} (${t('users.positionTimeline.reason.lbs')})`
        case 'no_location_data':
            return `${base} (${t('users.positionTimeline.reason.noLocationData')})`
        case 'invalid_coordinates':
            return `${base} (${t('users.positionTimeline.reason.invalidCoordinates')})`
        default:
            return base
    }
}

export const accuracyLabel = (accuracy: number): string =>
    Number.isFinite(accuracy) && accuracy > 0 ? `±${Math.round(accuracy)}m` : ''

export const formatClock = (timestamp: string): string => {
    const date = new Date(timestamp)
    return Number.isNaN(date.getTime()) ? '--:--:--' : format(date, 'HH:mm:ss')
}

export const hasTimelineLocation = (
    item: IUserTimelineItem
): item is IUserTimelineItem & { location: ITimelineLocation } =>
    item.location !== null &&
    Number.isFinite(item.location.latitude) &&
    Number.isFinite(item.location.longitude)

export const sortTimeline = (items: IUserTimelineItem[]): IUserTimelineItem[] =>
    [...items].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime() || a.order - b.order
    )

export const summarizeTimeline = (items: IUserTimelineItem[]) => {
    const located = items.filter(hasTimelineLocation).length
    return { total: items.length, located, unavailable: items.length - located }
}

export const pathOf = (items: IUserTimelineItem[]): { lat: number; lng: number }[] =>
    items.filter(hasTimelineLocation).map(item => ({ lat: item.location.latitude, lng: item.location.longitude }))

export interface PlaybackState {
    activeIndex: number | null
    isPlaying: boolean
}

export type PlaybackAction =
    | { type: 'reset' }
    | { type: 'select'; index: number; length: number }
    | { type: 'toggle'; length: number }
    | { type: 'tick'; length: number }

export const initialPlayback: PlaybackState = { activeIndex: null, isPlaying: false }

export const playbackReducer = (state: PlaybackState, action: PlaybackAction): PlaybackState => {
    switch (action.type) {
        case 'reset':
            return initialPlayback
        case 'select':
            if (!Number.isInteger(action.index) || action.index < 0 || action.index >= action.length) return state
            return { activeIndex: action.index, isPlaying: false }
        case 'toggle':
            if (action.length === 0) return state
            if (state.isPlaying) return { ...state, isPlaying: false }
            return {
                activeIndex:
                    state.activeIndex === null || state.activeIndex >= action.length - 1 ? 0 : state.activeIndex,
                isPlaying: true
            }
        case 'tick': {
            if (!state.isPlaying || state.activeIndex === null) return state
            if (state.activeIndex >= action.length - 1) return { ...state, isPlaying: false }
            const next = state.activeIndex + 1
            return { activeIndex: next, isPlaying: next < action.length - 1 }
        }
        default:
            return state
    }
}
